import {ApolloLink, Observable} from '@apollo/client';
import type {FetchResult} from '@apollo/client';

import type {
  InternalOperation,
  unstable_InternalOperation as InternalOperationRevamp,
} from '@sail/data';
import {RestLink as ApolloRestLink} from '../../forked/apollo-link-rest';
import {bodyBuilder} from './builders';
import type {
  PathBuilderParams,
  UnionObjectTypesMap,
  FieldVersionOverrides,
  GraphQlResponse,
} from './types';
import getPossibleTypes from '../../utils/getPossibleTypes';
import stringify from './stringify';
import {rebuildResponse} from './errorConversion';
import {mapUnionTypenames} from './mapUnionTypenames';
import type {PossibleTypes} from './mapUnionTypenames';
import buildRestPath from './buildRestPath';
import type {TopLevelFieldToArgs} from './removePbeFromParams';
import fragmentMatcher from './fragmentMatcher';
import {STRIPE_VERSION_HEADER} from '../../../addContextHeaders';
import type {InteropFields} from './rewriteInteropParams';

type Forward = (arg1: InternalOperation) => Observable<any>;

type AvailableInternalOperations = InternalOperation | InternalOperationRevamp;

const defaultSerializer = (data: any, headers: any) => {
  return {
    body: stringify(data),
    headers,
  };
};

const generateVersionOverrideHeaders = (
  overridesForOperationType: Record<string, string>,
  params: PathBuilderParams,
) => {
  const fieldName = params.fieldName;
  const shimmedVersion = overridesForOperationType?.[fieldName];
  return shimmedVersion ? {[STRIPE_VERSION_HEADER]: shimmedVersion} : undefined;
};

const headersBuilder = (
  fieldVersionOverrides: FieldVersionOverrides,
  params: PathBuilderParams,
) => {
  const operationType = params.context.mainDefinition.operation;
  return operationType === 'subscription'
    ? undefined
    : generateVersionOverrideHeaders(
        fieldVersionOverrides[operationType],
        params,
      );
};

export type Observer<GraphQlError> = {
  next: (value: {data: any; errors?: GraphQlError[]}) => void;
  error: (error: unknown) => void;
  complete: () => void;
};

export type ResponseErrorsParams<GraphQlError> = {
  response: GraphQlResponse<GraphQlError>;
  observer: Observer<GraphQlError>;
};

export type MapUnionTypenamesErrorParams<
  GraphQlError,
  InternalOperation extends AvailableInternalOperations,
> = {
  error: unknown;
  observer: Observer<GraphQlError>;
  operation: InternalOperation;
};

export type ErrorCallbackParams<
  GraphQlError,
  InternalOperation extends AvailableInternalOperations,
> = {
  error: unknown;
  observer: Observer<GraphQlError>;
  operation: InternalOperation;
};

export type ErrorHandlerStrategy<
  GraphQlError,
  InternalOperation extends AvailableInternalOperations,
> = {
  handleResponseErrors: (params: ResponseErrorsParams<GraphQlError>) => void;
  handleMapUnionTypenamesError: (
    params: MapUnionTypenamesErrorParams<GraphQlError, InternalOperation>,
  ) => void;
  handleErrorCallback: (
    params: ErrorCallbackParams<GraphQlError, InternalOperation>,
  ) => void;
};

export type RebuildErrorFromResponse<GraphQlError> = (
  maybeBody: any,
  maybeMeta: any,
) => GraphQlError;

export default class RestLink<
  GraphQlError,
  InternalOperation extends AvailableInternalOperations,
> extends ApolloLink {
  restLink: ApolloLink;

  unionObjectTypesMap: UnionObjectTypesMap;

  pbeMap: Record<string, ReadonlyArray<string>>;

  queryEndpointsToBeRequestedWithPost?: string[];

  possibleTypes: PossibleTypes;

  shadowComparisonOperationsV1ToV2?: Record<string, boolean>;

  topLevelFieldToArgs: TopLevelFieldToArgs;

  fieldVersionOverrides: FieldVersionOverrides;

  errorHandler: ErrorHandlerStrategy<GraphQlError, InternalOperation>;

  rebuildErrorFromResponse: RebuildErrorFromResponse<GraphQlError>;

  interopFields?: InteropFields;

  constructor(
    unionObjectTypesMap: UnionObjectTypesMap,
    pbeMap: Record<string, ReadonlyArray<string>>,
    topLevelFieldToArgs: TopLevelFieldToArgs,
    fieldVersionOverrides: FieldVersionOverrides,
    errorHandler: ErrorHandlerStrategy<GraphQlError, InternalOperation>,
    rebuildErrorFromResponse: RebuildErrorFromResponse<GraphQlError>,
    queryEndpointsToBeRequestedWithPost?: string[],
    shadowComparisonOperationsV1ToV2?: Record<string, boolean>,
    interopFields?: InteropFields,
  ) {
    super();

    this.unionObjectTypesMap = unionObjectTypesMap;
    this.pbeMap = pbeMap;
    this.queryEndpointsToBeRequestedWithPost =
      queryEndpointsToBeRequestedWithPost;
    this.possibleTypes = getPossibleTypes(unionObjectTypesMap);
    this.shadowComparisonOperationsV1ToV2 = shadowComparisonOperationsV1ToV2;
    this.topLevelFieldToArgs = topLevelFieldToArgs;
    this.fieldVersionOverrides = fieldVersionOverrides;
    this.errorHandler = errorHandler;
    this.rebuildErrorFromResponse = rebuildErrorFromResponse;
    this.interopFields = interopFields;

    this.restLink = new ApolloRestLink({
      uri: '/',
      customFetch: undefined, // `ApolloRestLink` reads `fetch` from Apollo context.
      defaultSerializer,
      headersBuilder: headersBuilder.bind(
        undefined,
        this.fieldVersionOverrides,
      ),
      fragmentMatcher: fragmentMatcher.bind(
        undefined,
        this.unionObjectTypesMap,
      ),
    });
  }

  request(operation: InternalOperation, forward?: Forward): Observable<any> {
    operation.variables = {
      ...(operation.variables || {}),

      pathBuilder: (params: PathBuilderParams) => {
        return buildRestPath(
          params,
          operation,
          this.pbeMap,
          this.topLevelFieldToArgs,
          this.queryEndpointsToBeRequestedWithPost,
          this.shadowComparisonOperationsV1ToV2,
          this.interopFields,
        );
      },

      bodyBuilder,
    };

    return new Observable((observer: any) => {
      const observableFromRestLink = this.restLink.request(operation, forward);

      if (observableFromRestLink) {
        observableFromRestLink.subscribe({
          next: (res: FetchResult) => {
            const response = rebuildResponse(
              res as GraphQlResponse<GraphQlError>,
              this.rebuildErrorFromResponse,
            );

            if (response.errors?.length) {
              this.errorHandler.handleResponseErrors({
                response,
                observer,
              });
            } else {
              try {
                const data = mapUnionTypenames(
                  this.unionObjectTypesMap,
                  this.possibleTypes,
                  response.data,
                );

                observer.next({
                  data,
                  errors: response.errors,
                });
              } catch (error: unknown) {
                this.errorHandler.handleMapUnionTypenamesError({
                  error,
                  observer,
                  operation,
                });

                return;
              }
            }

            observer.complete();
          },

          error: (error: unknown) => {
            this.errorHandler.handleErrorCallback({
              error,
              observer,
              operation,
            });
          },
        });
      }
    });
  }
}
